import type {
    RevenueAnalyticsFilterColumn,
    RevenueAnalyticsSortColumn,
} from 'src/admin/models/RevenueAnalyticsModel/state';

export type RevenueAnalyticsColumnKey =
    | 'provider'
    | 'symbol'
    | 'qty'
    | 'avgPrice'
    | 'totalCost'
    | 'curPrice'
    | 'fairValue'
    | 'sodInventory'
    | 'remainingInventory'
    | 'percentUsed'
    | 'percentRemaining';

export interface RevenueAnalyticsColumn {
    readonly key: RevenueAnalyticsColumnKey;
    readonly align: 'left' | 'right';
    /** CSS grid track for the column; the table's template is the visible tracks joined. */
    readonly width: string;
    /** Present when the backend can sort by this column. */
    readonly sortColumn?: RevenueAnalyticsSortColumn;
    /** Present when the column has a header filter. */
    readonly filterColumn?: RevenueAnalyticsFilterColumn;
    /** Whether the user may hide the column; the symbol column anchors every row and stays. */
    readonly hideable: boolean;
}

/**
 * Single source of truth for the table's columns, in display order. New
 * columns are added here (plus a label and a cell in the page) and pick up
 * sorting, filtering, and show/hide wiring from the registry.
 */
export const REVENUE_ANALYTICS_COLUMNS = [
    { key: 'provider', align: 'left', width: 'minmax(7rem,1.1fr)', sortColumn: 'provider', hideable: true },
    { key: 'symbol', align: 'left', width: 'minmax(4.5rem,.8fr)', sortColumn: 'symbol', hideable: false },
    {
        key: 'qty',
        align: 'right',
        width: 'minmax(4.5rem,.7fr)',
        sortColumn: 'qty',
        filterColumn: 'qty',
        hideable: true,
    },
    {
        key: 'avgPrice',
        align: 'right',
        width: 'minmax(4.5rem,.7fr)',
        sortColumn: 'avgPrice',
        filterColumn: 'avgPrice',
        hideable: true,
    },
    {
        key: 'totalCost',
        align: 'right',
        width: 'minmax(5rem,.8fr)',
        sortColumn: 'totalCost',
        filterColumn: 'totalCost',
        hideable: true,
    },
    {
        key: 'curPrice',
        align: 'right',
        width: 'minmax(4.5rem,.7fr)',
        sortColumn: 'curPrice',
        filterColumn: 'curPrice',
        hideable: true,
    },
    { key: 'fairValue', align: 'right', width: 'minmax(4.5rem,.7fr)', hideable: true },
    { key: 'sodInventory', align: 'right', width: 'minmax(4.5rem,.7fr)', hideable: true },
    { key: 'remainingInventory', align: 'right', width: 'minmax(4.5rem,.7fr)', hideable: true },
    { key: 'percentUsed', align: 'right', width: 'minmax(6rem,.9fr)', hideable: true },
    { key: 'percentRemaining', align: 'right', width: 'minmax(6rem,.9fr)', hideable: true },
] as const satisfies ReadonlyArray<RevenueAnalyticsColumn>;

export type RevenueAnalyticsColumnVisibility = Readonly<Record<RevenueAnalyticsColumnKey, boolean>>;

export const DEFAULT_REVENUE_ANALYTICS_COLUMN_VISIBILITY: RevenueAnalyticsColumnVisibility = Object.fromEntries(
    REVENUE_ANALYTICS_COLUMNS.map((column) => [column.key, true]),
) as Record<RevenueAnalyticsColumnKey, boolean>;

export const findRevenueAnalyticsColumn = (key: RevenueAnalyticsColumnKey): RevenueAnalyticsColumn =>
    REVENUE_ANALYTICS_COLUMNS.find((column) => column.key === key)!;

/**
 * Merges a persisted visibility map over the defaults: unknown keys (from a
 * column that no longer exists) are dropped, columns added since the map was
 * saved default to visible, and non-hideable columns are forced visible.
 */
export function sanitizeRevenueAnalyticsColumnVisibility(
    stored: Readonly<Record<string, boolean>>,
): RevenueAnalyticsColumnVisibility {
    const sanitized = Object.fromEntries(
        REVENUE_ANALYTICS_COLUMNS.map((column) => [column.key, column.hideable ? (stored[column.key] ?? true) : true]),
    ) as Record<RevenueAnalyticsColumnKey, boolean>;
    // Hand back the shared default when nothing is hidden so callers can
    // detect "all visible" by reference.
    return isEveryRevenueAnalyticsColumnVisible(sanitized) ? DEFAULT_REVENUE_ANALYTICS_COLUMN_VISIBILITY : sanitized;
}

export const isEveryRevenueAnalyticsColumnVisible = (visibility: RevenueAnalyticsColumnVisibility): boolean =>
    REVENUE_ANALYTICS_COLUMNS.every((column) => visibility[column.key]);

export const visibleRevenueAnalyticsColumns = (
    visibility: RevenueAnalyticsColumnVisibility,
): ReadonlyArray<RevenueAnalyticsColumn> => REVENUE_ANALYTICS_COLUMNS.filter((column) => visibility[column.key]);

/** `grid-template-columns` value for the given columns. */
export const revenueAnalyticsGridTemplate = (columns: ReadonlyArray<RevenueAnalyticsColumn>): string =>
    columns.map((column) => column.width).join(' ');

export type AggregatedSymbolsColumnKey = 'symbol' | 'qty' | 'avgPrice' | 'totalCost' | 'curPrice' | 'fairValue';

export interface AggregatedSymbolsColumn {
    readonly key: AggregatedSymbolsColumnKey;
    readonly align: 'left' | 'right';
    /** CSS grid track for the column; the table's template is the visible tracks joined. */
    readonly width: string;
    /** Present when the backend can sort by this column. */
    readonly sortColumn?: RevenueAnalyticsSortColumn;
    /** Present when the column has a header filter. */
    readonly filterColumn?: RevenueAnalyticsFilterColumn;
    /** Whether the user may hide the column; the symbol column anchors every row and stays. */
    readonly hideable: boolean;
}

/**
 * The Aggregated Symbols tab's columns, in display order — the provider
 * rows' shared metrics; provider identity and inventory do not survive the
 * per-symbol grouping.
 */
export const AGGREGATED_SYMBOLS_COLUMNS = [
    { key: 'symbol', align: 'left', width: 'minmax(4.5rem,.9fr)', sortColumn: 'symbol', hideable: false },
    {
        key: 'qty',
        align: 'right',
        width: 'minmax(4.5rem,.8fr)',
        sortColumn: 'qty',
        filterColumn: 'qty',
        hideable: true,
    },
    {
        key: 'avgPrice',
        align: 'right',
        width: 'minmax(4.5rem,.8fr)',
        sortColumn: 'avgPrice',
        filterColumn: 'avgPrice',
        hideable: true,
    },
    {
        key: 'totalCost',
        align: 'right',
        width: 'minmax(5rem,.9fr)',
        sortColumn: 'totalCost',
        filterColumn: 'totalCost',
        hideable: true,
    },
    {
        key: 'curPrice',
        align: 'right',
        width: 'minmax(4.5rem,.8fr)',
        sortColumn: 'curPrice',
        filterColumn: 'curPrice',
        hideable: true,
    },
    { key: 'fairValue', align: 'right', width: 'minmax(4.5rem,.8fr)', hideable: true },
] as const satisfies ReadonlyArray<AggregatedSymbolsColumn>;

export type AggregatedSymbolsColumnVisibility = Readonly<Record<AggregatedSymbolsColumnKey, boolean>>;

export const DEFAULT_AGGREGATED_SYMBOLS_COLUMN_VISIBILITY: AggregatedSymbolsColumnVisibility = Object.fromEntries(
    AGGREGATED_SYMBOLS_COLUMNS.map((column) => [column.key, true]),
) as Record<AggregatedSymbolsColumnKey, boolean>;

export const findAggregatedSymbolsColumn = (key: AggregatedSymbolsColumnKey): AggregatedSymbolsColumn =>
    AGGREGATED_SYMBOLS_COLUMNS.find((column) => column.key === key)!;

/** Like {@link sanitizeRevenueAnalyticsColumnVisibility}, over the Aggregated Symbols registry. */
export function sanitizeAggregatedSymbolsColumnVisibility(
    stored: Readonly<Record<string, boolean>>,
): AggregatedSymbolsColumnVisibility {
    const sanitized = Object.fromEntries(
        AGGREGATED_SYMBOLS_COLUMNS.map((column) => [column.key, column.hideable ? (stored[column.key] ?? true) : true]),
    ) as Record<AggregatedSymbolsColumnKey, boolean>;
    return isEveryAggregatedSymbolsColumnVisible(sanitized) ? DEFAULT_AGGREGATED_SYMBOLS_COLUMN_VISIBILITY : sanitized;
}

export const isEveryAggregatedSymbolsColumnVisible = (visibility: AggregatedSymbolsColumnVisibility): boolean =>
    AGGREGATED_SYMBOLS_COLUMNS.every((column) => visibility[column.key]);

export const visibleAggregatedSymbolsColumns = (
    visibility: AggregatedSymbolsColumnVisibility,
): ReadonlyArray<AggregatedSymbolsColumn> => AGGREGATED_SYMBOLS_COLUMNS.filter((column) => visibility[column.key]);

/** `grid-template-columns` value for the given columns. */
export const aggregatedSymbolsGridTemplate = (columns: ReadonlyArray<AggregatedSymbolsColumn>): string =>
    columns.map((column) => column.width).join(' ');
